import { type ReactElement } from "react";
import Done from "@mui/icons-material/Done";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Box, Tooltip, useTheme } from "@mui/material";

import type { NhsNumberIndicatorStatus } from "@thalamos/aspire-common";

export type IndicatorStatusProps = {
  status: NhsNumberIndicatorStatus["value"];
  systemName?: string;
};

export function IndicatorStatus({ status, systemName }: IndicatorStatusProps) {
  const theme = useTheme();
  const statusConfig: Partial<
    Record<
      NhsNumberIndicatorStatus["value"],
      {
        icon: ReactElement;
        tooltip: string;
        color: string;
        boxed: boolean;
      }
    >
  > = {
    "01": {
      icon: <Done fontSize="small" />,
      tooltip: "NHS number is verified by PDS (the Spine)",
      color: theme.palette.indicatorStatuses.verified,
      boxed: true,
    },
    "02": {
      icon: <Done fontSize="small" />,
      tooltip: `NHS number is verified by Rio${systemName ? ` (${systemName})` : ""}, but not yet verified by PDS (the Spine)`,
      color: theme.palette.indicatorStatuses.rioVerified,
      boxed: true,
    },
    "03": {
      icon: <Done fontSize="small" />,
      tooltip:
        "NHS number is spine linked but hasn't been traced in the last 90 days",
      color: theme.palette.indicatorStatuses.notTracedRecently,
      boxed: true,
    },
    "04": {
      icon: <WarningAmberOutlined fontSize="small" />,
      tooltip:
        "NHS Number no longer valid and will no longer be synchronised with PDS, please refer to your back office for processing",
      color: theme.palette.indicatorStatuses.invalid,
      boxed: false,
    },
    "05": {
      icon: <WarningAmberOutlined fontSize="small" />,
      tooltip:
        "NHS Number has been replaced (superseded) in PDS. This new NHS number is already linked to an existing patient record in eMHA and should be merged together",
      color: theme.palette.indicatorStatuses.superseded,
      boxed: false,
    },
  };

  const config = statusConfig[status];
  if (!config) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", pl: 1 }}>
      <Tooltip
        title={config.tooltip}
        arrow
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.common.white,
              color: theme.palette.common.blueGray,
              border: `1px solid ${theme.palette.common.lightGray}`,
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              fontSize: 10,
              padding: theme.spacing(1),
              whiteSpace: "pre-line",
              "& .MuiTooltip-arrow": {
                color: theme.palette.common.white,
                "&::before": {
                  border: `1px solid ${theme.palette.common.lightGray}`,
                  boxSizing: "border-box",
                },
              },
            },
          },
        }}
      >
        {config.boxed ? (
          <Box
            data-testid="status-icon-box"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: config.color,
              color: theme.palette.common.white,
              width: 20,
              height: 20,
              borderRadius: "50%",
            }}
          >
            {config.icon}
          </Box>
        ) : (
          <Box data-testid="status-icon-box" sx={{ color: config.color }}>
            {config.icon}
          </Box>
        )}
      </Tooltip>
    </Box>
  );
}
